import { Images } from "../../../../../utils/imageConstants";
import {
  formatDateAndTime,
  formatMatchDate,
  formatMatchVenueLine,
  formatTime,
  formatTournamentDateWindowShort,
  formatUpcomingMatchDayLabel,
  getPlayerImageUrl,
  getTimeUntilMatch,
  parseMatchInstant,
} from "../../../../../helper/helper";
import type { IMatchSummary } from "./types/IMatches";
import { useNavigate } from "react-router-dom";
import { RiPencilLine } from "react-icons/ri";

type UpcomingMatchProps = {
  matches: IMatchSummary[];
  isLoading: boolean;
  viewList: boolean;
  /** League managers can open the schedule form prefilled for this match (same scheduleMatch API). */
  canEditMatch?: boolean;
  /** Opens edit UI (e.g. modal) instead of navigating away. */
  onEditMatch?: (matchId: string) => void;
};

const UpcomingMatches = ({
  matches,
  isLoading,
  viewList,
  canEditMatch = false,
  onEditMatch,
}: UpcomingMatchProps) => {
  const navigate = useNavigate();

  if (isLoading) {
    return <p>Loading…</p>;
  }

  const data: IMatchSummary[] = matches && matches.length > 0 ? matches : [];

  return (
    <div>
      {viewList ? (
        <div className="cards-container">
          {data.map((match: IMatchSummary) => {
            const scheduleRaw = match?.dateTime || match?.startDate || null;
            const scheduleDate = parseMatchInstant(scheduleRaw);
            const listDate = scheduleDate
              ? `${scheduleDate.getDate()} ${scheduleDate
                  .toLocaleDateString("en-US", { month: "short" })
                  .toUpperCase()}, ${scheduleDate
                  .toLocaleDateString("en-US", { weekday: "short" })
                  .toUpperCase()}`
              : "—";
            const listTime = formatTime(scheduleRaw);
            const [listClock, listAmPm] = listTime.time.split(" ");
            return (
            <div className="cards" key={match?.matchId}>
              <article className="match-card">
                <div className="match-card-meta-with-actions">
                  <div className="meta">
                    <span className="tag">{match?.matchType || "-"}</span>
                    <div className="details">
                      <p>
                        <strong>{formatDateAndTime(match?.tournament?.startDate)}</strong> | {match?.leagueName || "-"}
                      </p>
                      <p className="sub">Umpire :</p>
                      <p className="sub">{match?.city || "-"}</p>
                    </div>
                  </div>
                  {canEditMatch && match?.matchId ? (
                    <button
                      type="button"
                      className="match-edit-icon-btn"
                      aria-label="Edit match"
                      onClick={() =>
                        onEditMatch
                          ? onEditMatch(match.matchId)
                          : navigate(
                            `/tournaments/schedule-match?matchId=${encodeURIComponent(match.matchId)}`,
                          )
                      }
                    >
                      <RiPencilLine size={18} aria-hidden />
                    </button>
                  ) : null}
                </div>

                <div className="upcoming-teams">
                  <div className="upcoming-teams-rows">
                    <div className="upcoming-row">
                      <img className="logo" src={Images.card_info_image} alt="" />
                      <div className="name-and-chip">
                        <span className="team">{match?.teamOne?.teamName}</span>
                        <span className="chip-blue">Home</span>
                      </div>
                    </div>

                    <div className="upcoming-row">
                      <img className="logo" src={Images.card_info_image} alt="" />
                      <div className="name-and-chip">
                        <span className="team">{match?.teamTwo?.teamName}</span>
                        <span className="chip-blue">Away</span>
                      </div>
                    </div>
                  </div>
                  <div className="time-data">
                    <p className="date">{listDate}</p>
                    <p className="time">
                      <span className="hours">{listClock || "—"}</span>
                      {listAmPm ? <span className="ampm">{listAmPm}</span> : null}
                    </p>
                  </div>
                </div>

                <div className="divider" aria-hidden="true"></div>

                <div className="cta">
                  <button className="btn btn-secondary">Match Center &nbsp; &gt;</button>
                </div>
              </article>
            </div>
            );
          })}
        </div>
      ) : (
        <div className="live-matches-grid">
          {data.map((match: IMatchSummary) => {
            const scheduleRaw = match?.dateTime || match?.startDate || null;
            const tournamentWindowLine = formatTournamentDateWindowShort(match);
            const timeInfo = formatTime(scheduleRaw);
            const timeUntil = getTimeUntilMatch(scheduleRaw);
            const matchDate = scheduleRaw
              ? formatMatchDate(scheduleRaw)
              : tournamentWindowLine || "—";
            const dayLabel = scheduleRaw
              ? formatUpcomingMatchDayLabel(scheduleRaw)
              : tournamentWindowLine
                ? `Tournament: ${tournamentWindowLine}`
                : "—";
            const displayClockTime = scheduleRaw ? timeInfo.time : "TBD";
            return (
              <div className="live-match-card" key={match?.matchId}>
                <div className="live-match-content">
                  <div className="live-match-header">
                    <div className="live-match-header-row">
                      <div className="live-indicator">
                        <span className="upcoming-tag">UPCOMING</span>
                        <span className="match-date">{matchDate}</span>
                        <div className="match-info">
                          <span className="match-context-league">
                            {match?.leagueName?.trim() || "—"}
                          </span>
                          <span className="separator">|</span>
                          <span className="match-context-tournament">
                            {match?.tournament?.tournamentName?.trim() || "—"}
                          </span>
                        </div>
                      </div>
                      {canEditMatch && match?.matchId ? (
                        <button
                          type="button"
                          className="match-edit-icon-btn"
                          aria-label="Edit match"
                          onClick={() =>
                            onEditMatch
                              ? onEditMatch(match.matchId)
                              : navigate(
                                `/tournaments/schedule-match?matchId=${encodeURIComponent(match.matchId)}`,
                              )
                          }
                        >
                          <RiPencilLine size={18} aria-hidden />
                        </button>
                      ) : null}
                    </div>
                    <div className="divider-line"></div>
                    <div className="teams-and-time-section">
                      <div className="teams-section">
                        <div className="team-row">
                          <div className="team-info">
                            <div className="team-logo">
                              <img
                                src={getPlayerImageUrl(match?.teamOne?.imageUrl) || Images.card_info_image}
                                alt="Team Logo"
                              />
                            </div>
                            <span className="team-name">{match?.teamOne?.teamName}</span>
                          </div>
                          {/* <div className="team-score" aria-hidden>
                            <span className="score">—</span>
                          </div> */}
                        </div>

                        <div className="team-row">
                          <div className="team-info">
                            <div className="team-logo">
                              <img
                                src={getPlayerImageUrl(match?.teamTwo?.imageUrl) || Images.card_info_image}
                                alt="Team Logo"
                              />
                            </div>
                            <span className="team-name">{match?.teamTwo?.teamName}</span>
                          </div>
                          {/* <div className="team-score" aria-hidden>
                            <span className="score">—</span>
                          </div> */}
                        </div>
                      </div>

                      <div className="time-section">
                        <div className="time-info">
                          <span className="time-label">{dayLabel}</span>
                          <span className="time-value">{displayClockTime}</span>
                        </div>
                      </div>
                    </div>
                    <div className="countdown-section">
                      <span className="countdown-text">
                        {timeUntil.isUnknown ? (
                          <>
                            {tournamentWindowLine
                              ? "Kickoff time not set for this fixture"
                              : "Schedule TBD"}
                          </>
                        ) : timeUntil.isPast ? (
                          <>Match time reached</>
                        ) : (
                          <>
                            Match starts in{" "}
                            {timeUntil.days > 0 && (
                              <>
                                <span className="countdown-bold">{timeUntil.days}</span> days{" "}
                              </>
                            )}
                            <span className="countdown-bold">{timeUntil.hours}</span> hrs{" "}
                            <span className="countdown-bold">{timeUntil.minutes}</span> min
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="match-details">
                    <p className="umpire-info">
                      <span className="label">Umpire : </span>
                      <span className="value">
                        {match?.umpire?.trim() || match?.umpireName?.trim() || "TBD"}
                      </span>
                    </p>
                    <div className="venue-info">
                      <div className="venue-icon">
                        <img src={Images.blue_location} alt="Venue" />
                      </div>
                      <span className="venue-name">{formatMatchVenueLine(match)}</span>
                    </div>
                  </div>
                  <div className="divider-line"></div>
                  <div className="upcoming-match-actions">
                    <button
                      className="match-center-btn"
                      onClick={() => navigate(`/matches-details/${match?.matchId}`)}
                    >
                      <span>Match Center</span>
                      <div className="btn-icon">
                        <img src={Images.scorecard_icon} alt="Arrow" />
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {matches?.length === 0 &&
        <div className="no-data-found">
          <img src={Images.my_leagues_no} className="n" alt="no-data-image" />
          <p>NO MATCHES FOUND</p>
        </div>
      }
    </div>
  );
};

export default UpcomingMatches;
